import type { TokenPair } from '@core/types/api.types.ts';
import type { UsersOutputDTO, CompaniesOutputDTO } from '@core/types/auth.types.ts';

const ACCESS_TOKEN_KEY = 'dw_access_token';
const REFRESH_TOKEN_KEY = 'dw_refresh_token';
const TOKEN_EXPIRY_KEY = 'dw_token_expiry';
const USER_DATA_KEY = 'dw_user_data';
const COMPANY_DATA_KEY = 'dw_company_data';

/**
 * Almacenamiento de tokens. Todo en localStorage para que la sesión se
 * comparta entre pestañas del mismo navegador (una pestaña nueva detecta
 * la sesión ya iniciada en otra pestaña).
 */
export const tokenStorage = {
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setTokens(tokens: TokenPair): void {
    const expiresAt = Date.now() + tokens.expiresIn * 1000;
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiresAt));
  },

  /** Store a raw JWT (ClickPalm API returns a single token string). Decodes `exp` from the payload. */
  setRawJwt(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    const parts = token.split('.');
    let expiresAt = Date.now() + 24 * 60 * 60 * 1000; // default 24h
    if (parts.length === 3) {
      try {
        const payload = JSON.parse(atob(parts[1])) as { exp?: number };
        if (payload.exp) expiresAt = payload.exp * 1000;
      } catch { /* malformed JWT — use default expiry */ }
    }
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiresAt));
  },

  setUserData(user: UsersOutputDTO): void {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
  },

  getUserData(): UsersOutputDTO | null {
    const raw = localStorage.getItem(USER_DATA_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as UsersOutputDTO; }
    catch { return null; }
  },

  setCompanyData(company: CompaniesOutputDTO): void {
    localStorage.setItem(COMPANY_DATA_KEY, JSON.stringify(company));
  },

  getCompanyData(): CompaniesOutputDTO | null {
    const raw = localStorage.getItem(COMPANY_DATA_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as CompaniesOutputDTO; }
    catch { return null; }
  },

  clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(COMPANY_DATA_KEY);
  },

  isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiresAt) return true;
    // Buffer de 30 segundos antes de expiración real
    return Date.now() >= Number(expiresAt) - 30_000;
  },

  getExpiresAt(): number | null {
    const val = localStorage.getItem(TOKEN_EXPIRY_KEY);
    return val ? Number(val) : null;
  },
};
